/**
 * Delimited-text primitives. Phase 2, the foundation every CSV parser sits on.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3).
 *
 * These live in `io/` rather than `domain/` because parsing broker output is an
 * input concern, not a rule. They are still pure, so they are directly testable
 * — which matters more here than anywhere else: a parser bug does not throw, it
 * silently produces a trade with the wrong number in it.
 */

/**
 * Split one delimited line, honouring RFC-4180 quoting.
 *
 * A doubled quote inside a quoted field is a literal quote. Broker exports put
 * commas inside quoted note fields routinely, so a naive `split(',')` shifts
 * every column after the first note — silently, and only on the rows that have
 * one.
 */
export function parseLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = false;
      else cur += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === delim) { out.push(cur); cur = ''; }
      else cur += c;
    }
  }
  out.push(cur);
  return out;
}

/**
 * Delimited text → array of header-keyed rows.
 *
 * Blank lines are dropped anywhere in the file, not just at the end — some
 * exports separate sections with them. Cells are trimmed; a row shorter than
 * the header gets `''` for the missing columns rather than `undefined`, so
 * downstream `.trim()` calls cannot throw on a ragged export.
 */
export function parseDelimited(text: string, delim: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const headers = parseLine(lines[0] as string, delim).map((h) => h.trim());
  const out: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseLine(lines[i] as string, delim);
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j] as string] = (cells[j] || '').trim();
    }
    out.push(row);
  }
  return out;
}

/**
 * A date cell → `YYYY-MM-DD`.
 *
 * Order matters: ISO-like is tried FIRST, then US `M/D/YYYY`. Reversing them
 * would read `2026-07-15` as month 2026. The final `new Date()` fallback is
 * locale-dependent and therefore a last resort, not a path to rely on.
 *
 * ⚠ Takes `today` as a parameter. The original called `todayISO()`, which makes
 * an unparseable date resolve differently tomorrow — untestable, and the same
 * reason wall-clock inputs are injected in `domain/` (D26).
 */
export function parseDateOnly(s: string | null | undefined, today: string): string {
  if (!s) return today;
  const m = s.match(/(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return `${m[1]}-${pad(+(m[2] as string))}-${pad(+(m[3] as string))}`;
  const m2 = s.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (m2) return `${m2[3]}-${pad(+(m2[1] as string))}-${pad(+(m2[2] as string))}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  return today;
}

/**
 * A time cell → `HH:MM:SS`, 24-hour. Empty string when unparseable.
 *
 * ⚠ SECONDS ARE PRESERVED, deliberately. An earlier version truncated to
 * `HH:MM`, which capped time-to-extreme analytics at 60-second resolution and
 * forced same-minute-clamp workarounds in the excursion engine. On a scalp,
 * minute resolution is no resolution. Do not "tidy" this back to HH:MM.
 *
 * 12-hour input is handled because ATAS exports it that way.
 */
export function parseTimeOnly(s: string | null | undefined): string {
  if (!s) return '';
  const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?/);
  if (!m) return '';
  let hour = +(m[1] as string);
  const min = m[2] as string;
  const sec = m[3] || '00';
  const ampm = (m[4] || '').toLowerCase();
  if (ampm === 'pm' && hour < 12) hour += 12;
  else if (ampm === 'am' && hour === 12) hour = 0;
  return `${pad(hour)}:${min}:${sec}`;
}

const pad = (n: number): string => String(n).padStart(2, '0');
